import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail } from "lucide-react";
import { LogoMark } from "@/components/brand/LogoMark";
import { useAuth } from "@/context/AuthContext";
import { fetchLeads, logoutUser } from "@/lib/api";

const th = "text-left text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium px-5 py-4";
const td = "px-5 py-4 text-sm font-light text-foreground/90 align-top";

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeads()
      .then(setLeads)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = async () => {
    await logoutUser().catch(() => {});
    setUser(false);
    navigate("/admin/login");
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 text-foreground">
            <LogoMark className="w-8 h-8" />
            <span className="leading-tight">
              <span className="block text-xs tracking-[0.25em] font-medium">CLEAR HORIZON</span>
              <span className="block text-[9px] tracking-[0.35em] text-muted-foreground">INQUIRIES DASHBOARD</span>
            </span>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-300"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <h1 className="font-serif text-3xl font-light mb-10">Inquiries ({leads.length})</h1>

        {loading ? (
          <p data-testid="admin-loading" className="text-sm text-muted-foreground">Loading...</p>
        ) : (
          <div data-testid="leads-table" className="border border-white/10 overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-white/5">
                <tr>
                  <th className={th}>Received</th>
                  <th className={th}>Name</th>
                  <th className={th}>Email</th>
                  <th className={th}>Phone</th>
                  <th className={th}>Interested In</th>
                  <th className={th}>Message</th>
                  <th className={th}>Reply</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {leads.length === 0 ? (
                  <tr><td colSpan={7} className={`${td} text-center text-muted-foreground py-12`}>No inquiries yet.</td></tr>
                ) : (
                  leads.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-white/[0.03] transition-colors duration-200">
                      <td className={td}>{new Date(l.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
                      <td className={td}>{l.name}</td>
                      <td className={td}>{l.email}</td>
                      <td className={td}>{l.phone}</td>
                      <td className={td}>{l.interest || "—"}</td>
                      <td className={`${td} max-w-md`}>{l.message}</td>
                      <td className={td}>
                        <a
                          data-testid={`lead-reply-${l.id}`}
                          href={`mailto:${l.email}?subject=${encodeURIComponent("Re: Your inquiry to Clear Horizon Consulting")}&body=${encodeURIComponent(`Dear ${l.name},\n\nThank you for reaching out to Clear Horizon Consulting.\n\n\n`)}`}
                          className="inline-flex items-center gap-2 text-xs text-primary border border-primary/40 px-4 py-2 hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                        >
                          <Mail className="w-3.5 h-3.5" /> Reply
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
